import { Button, Divider, Flex, TreeSelect } from 'antd';
import PropTypes from 'prop-types';
import { useRef, useState } from 'react';
import usePopupTriggerWidth from '@/common/hooks/use-popup-trigger-width';
import {
  getOperationEntityScopeTreeSelectValue,
  normalizeOperationAnalysisScope,
} from '@/common/utils/operation-analysis-scope';

const getSelectionCountPlaceholder = (omittedValues) => `已选择 ${omittedValues.length + 1} 项`;

const OperationEntityScopeSelect = ({ treeData, value, onChange }) => {
  const selectRef = useRef(null);
  const popupWidth = usePopupTriggerWidth(selectRef);
  const committedEntityIds = getOperationEntityScopeTreeSelectValue(value, treeData);
  const [draftEntityIds, setDraftEntityIds] = useState(committedEntityIds);
  const [open, setOpen] = useState(false);

  const closeAndDiscardDraft = () => {
    setDraftEntityIds(committedEntityIds);
    setOpen(false);
  };

  const handleOpenChange = (nextOpen) => {
    setDraftEntityIds(committedEntityIds);
    setOpen(nextOpen);
  };

  const handleConfirm = () => {
    onChange(
      normalizeOperationAnalysisScope({ type: 'entities', entityIds: draftEntityIds }, treeData),
    );
    setOpen(false);
  };

  return (
    <TreeSelect
      ref={selectRef}
      aria-label="选择门店"
      disabled={!treeData.length}
      maxTagCount={1}
      maxTagPlaceholder={getSelectionCountPlaceholder}
      notFoundContent="暂无可选门店"
      open={open}
      placeholder="选择门店"
      popupMatchSelectWidth={popupWidth}
      popupRender={(originNode) => (
        <Flex vertical>
          {originNode}
          <Divider size="small" />
          <Flex gap="small" justify="end">
            <Button size="small" onClick={closeAndDiscardDraft}>
              取消
            </Button>
            <Button
              disabled={!draftEntityIds.length}
              size="small"
              type="primary"
              onClick={handleConfirm}
            >
              确定
            </Button>
          </Flex>
        </Flex>
      )}
      showCheckedStrategy={TreeSelect.SHOW_PARENT}
      showSearch={{ treeNodeFilterProp: 'title' }}
      styles={{
        popup: {
          item: { minWidth: 0, overflow: 'hidden' },
          itemTitle: {
            display: 'block',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
          root: {
            boxSizing: 'border-box',
            maxWidth: 'calc(100vw - 16px)',
            overflowX: 'hidden',
            width: popupWidth,
          },
        },
        root: { width: '100%' },
      }}
      treeCheckable
      treeData={treeData}
      treeDefaultExpandAll
      value={open ? draftEntityIds : committedEntityIds}
      onChange={setDraftEntityIds}
      onOpenChange={handleOpenChange}
    />
  );
};

OperationEntityScopeSelect.propTypes = {
  treeData: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      storeId: PropTypes.string,
      title: PropTypes.node.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
  value: PropTypes.oneOfType([
    PropTypes.shape({
      type: PropTypes.oneOf(['all']).isRequired,
    }),
    PropTypes.shape({
      entityIds: PropTypes.arrayOf(PropTypes.string).isRequired,
      type: PropTypes.oneOf(['entities']).isRequired,
    }),
  ]).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default OperationEntityScopeSelect;
